import React from 'react';
import {
  FileText,
  MapPin,
  Clock,
  Tag,
  ArrowLeft
} from 'lucide-react';
import { Contract } from '../contracts/ContractsTable';
import { ContractStatus, statusConfig } from '../../lib/contract-status';

export interface ContractMetadataHeaderProps {
  contract: Contract;
  statusOverride?: ContractStatus;
  onBack?: () => void;
}

const stateLabels: Record<string, string> = {
  PR: 'Puerto Rico',
  FL: 'Florida',
  TX: 'Texas',
  CA: 'California',
  NY: 'New York',
  LA: 'Louisiana',
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const ContractMetadataHeader: React.FC<ContractMetadataHeaderProps> = ({
  contract,
  statusOverride,
  onBack,
}) => {
  const effectiveStatus = statusOverride || contract.status;
  const statusStyle = statusConfig[effectiveStatus] || statusConfig.uploaded;

  return (
    <div className="bg-white rounded-lg shadow-md" data-testid="contract-metadata-header">
      {/* Back navigation and title */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-md text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Back to contracts"
              data-testid="back-to-contracts"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary-600 flex-shrink-0" />
              <div className="min-w-0">
                <h1
                  className="text-2xl font-bold text-gray-900 truncate"
                  data-testid="contract-title"
                >
                  {contract.name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle.bgColor} ${statusStyle.color}`}
                    data-testid="contract-status-badge"
                  >
                    {statusStyle.label}
                  </span>
                  {contract.version && (
                    <span
                      className="text-sm text-gray-500"
                      data-testid="contract-version"
                    >
                      Version {contract.version}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata grid */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
          {/* State/Territory */}
          <div className="flex items-start gap-3" data-testid="contract-state-info">
            <div className="p-2 bg-primary-100 rounded-lg">
              <MapPin className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">State/Territory</p>
              <p className="text-sm text-gray-900">
                {stateLabels[contract.state] || contract.state}
              </p>
            </div>
          </div>

          {/* Last Updated */}
          <div className="flex items-start gap-3" data-testid="contract-updated-info">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Clock className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Last Updated</p>
              <p className="text-sm text-gray-900">
                {formatDateTime(contract.updated_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Analysis info if available */}
        {(contract.analysis_status || contract.gap_count !== undefined) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-4">
              {contract.analysis_status && (
                <div className="flex items-center gap-2" data-testid="contract-analysis-status">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Analysis:</span>
                  <span className={`text-sm font-medium ${
                    contract.analysis_status === 'completed'
                      ? 'text-success-600'
                      : contract.analysis_status === 'in_progress'
                      ? 'text-blue-600'
                      : 'text-gray-600'
                  }`}>
                    {contract.analysis_status === 'completed'
                      ? 'Completed'
                      : contract.analysis_status === 'in_progress'
                      ? 'In Progress'
                      : contract.analysis_status}
                  </span>
                </div>
              )}
              {contract.gap_count !== undefined && contract.gap_count > 0 && (
                <div className="flex items-center gap-2" data-testid="contract-gap-count">
                  <span className="text-sm text-gray-500">Gaps Found:</span>
                  <span className="px-2 py-0.5 bg-warning-100 text-warning-700 text-sm font-medium rounded-full">
                    {contract.gap_count}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
